/**
 * Service IA pour le chatbot WON
 * Gestion des appels OpenAI avec contexte métier
 */

interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface ChatbotConfig {
  apiKey: string;
  model: string;
  maxTokens: number;
  temperature: number;
}

interface ChatResponse {
  content: string;
  quickActions?: QuickAction[];
  isError?: boolean;
  errorMessage?: string;
}

interface QuickAction {
  id: string;
  label: string;
  emoji: string;
  category?: string;
}

class ChatbotAIService {
  private config: ChatbotConfig;
  private conversationHistory: ChatMessage[] = [];
  private readonly systemPrompt: string;
  private readonly preferBackend: boolean;
  private readonly useProxy: boolean;
  private readonly apiBaseUrl?: string;

  constructor(apiKey: string) {
    this.config = {
      apiKey,
      model: 'gpt-3.5-turbo', // Plus rapide et limites moins strictes
      maxTokens: 400, // Réduit pour éviter les limites
      temperature: 0.7
    };

    this.systemPrompt = this.buildSystemPrompt();
    // Préférer le backend si disponible (via env)
    const env: any = (typeof import.meta !== 'undefined' && (import.meta as any).env) ? (import.meta as any).env : {};
    this.preferBackend = true; // par défaut on tente le backend d'abord
    this.useProxy = env?.VITE_USE_API_PROXY === 'true' && env?.VITE_FORCE_API !== 'true';
    this.apiBaseUrl = env?.VITE_FORCE_API === 'true' ? (env?.VITE_API_BASE_URL || undefined) : undefined;
    this.initializeConversation();
  }

  private buildSystemPrompt(): string {
    return `Tu es WON, l'assistant expert de World Of Numeric, spécialisé en transformation digitale pour les entreprises françaises.

RÈGLE ABSOLUE : Ne jamais inventer ou supposer le secteur, la taille ou le contexte de l'entreprise si l'utilisateur ne l'a pas précisé. Si tu ne sais pas, demande !

PERSONNALITÉ :
- Consultant expert mais accessible
- Poses des questions pour comprendre le contexte AVANT de conseiller
- Donnes des exemples concrets mais ne supposes rien
- Redirige vers les vrais services World Of Numeric quand approprié

TON EXPERTISE :
• RGPD : Conformité, audits, éviter sanctions 4% CA
• Cybersécurité : Protection PME, formations, conformité ANSSI
• Sites web : Création, refonte, SEO, référencement
• Facturation électronique : Obligation 2024-2026, automatisation
• Transformation digitale : Audit, stratégie, accompagnement

STYLE DE RÉPONSE :
- Si contexte manque : Pose 1-2 questions pour comprendre
- Si contexte donné : Conseils précis et personnalisés
- Toujours proposer l'étape suivante concrète
- Maximum 100 mots pour rester percutant
- Finir par une question ou action claire

EXEMPLES CORRECTS :
❌ "Pour une entreprise comme la vôtre dans le e-commerce..." (si pas dit)
✅ "Pour vous conseiller au mieux sur le RGPD, pouvez-vous me dire dans quel secteur vous travaillez ?"

❌ "Dans votre entreprise de 30 personnes..." (si pas dit)
✅ "Combien de personnes travaillent dans votre entreprise ? Cela m'aide à adapter mes conseils."

REDIRECTION VERS SERVICES :
Quand l'utilisateur veut :
- Contacter WON → Propose formulaire de contact ou téléphone
- Audit/devis → Redirige vers la page services appropriée
- Prendre RDV → Propose le vrai système de prise de RDV

NE JAMAIS supposer d'informations non données !`;
  }

  private initializeConversation(): void {
    this.conversationHistory = [
      {
        role: 'system',
        content: this.systemPrompt
      }
    ];
  }

  /**
   * Génère une réponse IA contextuelle avec détection d'intention
   */
  async generateResponse(userMessage: string): Promise<ChatResponse> {
    try {
      // Détection d'intentions directes AVANT l'appel IA
      const directIntent = this.detectDirectIntent(userMessage);
      if (directIntent) {
        return directIntent;
      }

      // Ajouter le message utilisateur à l'historique
      this.conversationHistory.push({
        role: 'user',
        content: userMessage
      });

      // 1) Tenter le backend si disponible
      if (this.preferBackend) {
        try {
          const backendResp = await this.callBackend(userMessage);
          if (backendResp) {
            // Ajouter la réponse à l'historique pour cohérence
            this.conversationHistory.push({ role: 'assistant', content: backendResp.content });
            return backendResp;
          }
        } catch (be) {
          console.warn('⚠️ Backend chatbot indisponible, fallback OpenAI/front:', be);
        }
      }

      console.log('🔥 Tentative d\'appel OpenAI...');

      // Appel avec retry automatique
      const response = await this.callOpenAIWithRetry();

      if (!response.ok) {
        throw new Error(`Erreur API OpenAI: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0]?.message?.content || '';

      console.log('✅ Réponse OpenAI reçue:', aiResponse.substring(0, 100) + '...');

      // Ajouter la réponse à l'historique
      this.conversationHistory.push({
        role: 'assistant',
        content: aiResponse
      });

      // Générer des actions rapides contextuelles
      const quickActions = this.generateContextualActions(userMessage, aiResponse);

      return {
        content: aiResponse,
        quickActions,
        isError: false
      };

    } catch (error) {
      console.error('❌ Erreur ChatbotAIService:', error);

      // Messages d'erreur spécifiques
      let errorMessage = 'Erreur inconnue';
      if (error instanceof Error) {
        if (error.message.includes('429')) {
          errorMessage = 'Limite de requêtes atteinte - Utilisation du mode expert';
          console.log('⚠️ Limite OpenAI atteinte, passage en mode fallback intelligent');
        } else if (error.message.includes('401')) {
          errorMessage = 'Clé API invalide';
        } else if (error.message.includes('500')) {
          errorMessage = 'Serveur OpenAI temporairement indisponible';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        content: this.getFallbackResponse(userMessage),
        quickActions: this.getDefaultActions(),
        isError: true,
        errorMessage
      };
    }
  }

  /**
   * Streaming depuis le backend avec callbacks de delta (SSE/chunked)
   * Retourne la réponse finale, ou null si indisponible.
   */
  async generateResponseStreaming(
    userMessage: string,
    onDelta: (chunk: string) => void
  ): Promise<ChatResponse | null> {
    try {
      const streamed = await this.callBackendStream(userMessage, onDelta);
      if (streamed) {
        // Ajouter à l'historique
        this.conversationHistory.push({ role: 'user', content: userMessage });
        this.conversationHistory.push({ role: 'assistant', content: streamed.content });
        return streamed;
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  private async callBackendStream(
    userMessage: string,
    onDelta: (chunk: string) => void
  ): Promise<ChatResponse | null> {
    if (!this.preferBackend) return null;
    try {
      const base = this.useProxy ? '' : (this.apiBaseUrl || '');
      // Essais de chemins courants
      const endpoints = [
        `${base}/api/chatbot/stream`,
        `${base}/api/chatbot/message/stream`
      ];

      for (const url of endpoints) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: userMessage, history: this.conversationHistory.slice(-6) })
          });

          if (!response.ok || !response.body) {
            continue; // essayer prochain endpoint
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let done = false;
          let buffer = '';
          let full = '';

          while (!done) {
            const { value, done: d } = await reader.read();
            done = d || false;
            if (value) {
              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;
              // Support SSE (data:) ou texte brut chunké
              let idx;
              while ((idx = buffer.indexOf('\n')) >= 0) {
                const line = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 1);
                if (!line) continue;
                // SSE format: data: {"delta":"..."} ou data: ...
                if (line.startsWith('data:')) {
                  const payload = line.slice(5).trim();
                  try {
                    const j = JSON.parse(payload);
                    const delta = typeof j === 'string' ? j : (j.delta || j.content || '');
                    if (delta) {
                      full += delta;
                      onDelta(delta);
                    }
                  } catch {
                    full += payload;
                    onDelta(payload);
                  }
                } else {
                  // Texte brut
                  full += line;
                  onDelta(line);
                }
              }
            }
          }

          // Tentative de lecture du reste du buffer
          if (buffer.trim()) {
            const rest = buffer.trim();
            try {
              const j = JSON.parse(rest);
              const delta = typeof j === 'string' ? j : (j.delta || j.content || '');
              if (delta) {
                full += delta;
                onDelta(delta);
              }
            } catch {
              full += rest;
              onDelta(rest);
            }
          }

          return { content: full, isError: false };
        } catch (_e) {
          // essayer le prochain endpoint
        }
      }
      return null;
    } catch (_err) {
      return null;
    }
  }

  /**
   * Appelle l'endpoint backend si disponible. Retourne null si indisponible.
   */
  private async callBackend(userMessage: string): Promise<ChatResponse | null> {
    try {
      // Construction URL: via proxy '/api' en dev, sinon base URL
      const base = this.useProxy ? '' : (this.apiBaseUrl || '');
      const url = `${base}/api/chatbot/message`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, history: this.conversationHistory.slice(-6) })
      });

      if (!response.ok) {
        // 404/500: considérer comme indisponible pour fallback
        return null;
      }

      const data = await response.json();
      // Tolérer plusieurs schémas possibles ({message}, {content}, {data: {message}})
      const content = data?.message || data?.content || data?.data?.message || '';
      const quickActions: QuickAction[] | undefined = data?.quickActions;
      if (!content) return null;

      return { content, quickActions, isError: false };
    } catch (err) {
      return null;
    }
  }

  /**
   * Détecte les intentions directes pour redirection immédiate
   */
  private detectDirectIntent(userMessage: string): ChatResponse | null {
    const lowerMessage = userMessage.toLowerCase();

    // Intention CONTACT - Detection précise
    if (
      lowerMessage.includes('contacter won') ||
      lowerMessage.includes('contact won') ||
      lowerMessage.includes('joindre won') ||
      lowerMessage.includes('appeler won') ||
      (lowerMessage.includes('contact') && lowerMessage.includes('world')) ||
      lowerMessage === 'contact' ||
      lowerMessage === 'contacter' ||
      lowerMessage.includes('prendre contact') ||
      lowerMessage.includes('formulaire de contact')
    ) {
      return {
        content: "📞 **Contactez World Of Numeric :**\n\n🌐 **Formulaire en ligne :** Cliquez sur le bouton ci-dessous pour accéder directement à notre formulaire de contact\n📧 **Email :** contact@worldofnumeric.fr\n\n*Le formulaire s'ouvrira dans un nouvel onglet.*",
        quickActions: [
          { id: 'ouvrir_formulaire_contact', label: 'Ouvrir le formulaire', emoji: '🌐' }
        ]
      };
    }

    // Intention DEVIS
    if (
      lowerMessage.includes('devis') ||
      lowerMessage.includes('prix') ||
      lowerMessage.includes('tarif') ||
      lowerMessage.includes('coût') ||
      lowerMessage.includes('combien')
    ) {
      return {
        content: "💰 **Demande de devis World Of Numeric :**\n\nPour un devis personnalisé, utilisez notre formulaire dédié ou contactez-nous directement.\n\n📋 **Devis gratuit**\n🎯 **Adapté à vos besoins spécifiques**",
        quickActions: [
          { id: 'formulaire_devis', label: 'Demander un devis', emoji: '📋' },
          { id: 'ouvrir_formulaire_contact', label: 'Formulaire contact', emoji: '📞' }
        ]
      };
    }

    // Intention RDV
    if (
      lowerMessage.includes('rendez-vous') ||
      lowerMessage.includes('rdv') ||
      lowerMessage.includes('rencontrer') ||
      lowerMessage.includes('planifier')
    ) {
      return {
        content: "📅 **Planifier un rendez-vous :**\n\nNos experts sont disponibles pour vous rencontrer :\n\n🏢 **En présentiel** (région parisienne)\n💻 **En visioconférence** (toute la France)\n📞 **Par téléphone**\n\nContactez-nous pour planifier !",
        quickActions: [
          { id: 'ouvrir_formulaire_contact', label: 'Demander un RDV', emoji: '📅' },
        ]
      };
    }

    return null; // Pas d'intention directe détectée
  }

  /**
   * Appel OpenAI avec système de retry intelligent
   */
  private async callOpenAIWithRetry(maxRetries: number = 2): Promise<Response> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Tentative ${attempt}/${maxRetries}`);

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.config.apiKey}`
          },
          body: JSON.stringify({
            model: this.config.model,
            messages: this.conversationHistory.slice(-6), // Limiter l'historique pour éviter les tokens
            max_tokens: this.config.maxTokens,
            temperature: this.config.temperature,
            presence_penalty: 0.1,
            frequency_penalty: 0.1
          })
        });

        // Si succès, retourner
        if (response.ok) {
          return response;
        }

        // Si erreur 429 et pas dernier essai, attendre avant retry
        if (response.status === 429 && attempt < maxRetries) {
          const waitTime = Math.pow(2, attempt) * 1000; // Backoff exponentiel
          console.log(`⏳ Attente ${waitTime}ms avant retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          continue;
        }

        // Autres erreurs ou dernier essai
        return response;

      } catch (fetchError) {
        console.error(`❌ Erreur réseau tentative ${attempt}:`, fetchError);

        if (attempt === maxRetries) {
          throw fetchError;
        }

        // Attendre avant retry en cas d'erreur réseau
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }

    throw new Error('Échec de toutes les tentatives de connexion');
  }

  /**
   * Génère des actions rapides intelligentes qui progressent vers la conversion
   */
  private generateContextualActions(userMessage: string, aiResponse: string): QuickAction[] {
    const actions: QuickAction[] = [];
    const lowerMessage = userMessage.toLowerCase();
    const lowerResponse = aiResponse.toLowerCase();

    // Actions selon le besoin exprimé (pas supposé !)
    if (lowerMessage.includes('rgpd') || lowerMessage.includes('données') || lowerMessage.includes('conform')) {
      actions.push({ id: 'contact_rgpd', label: 'Contacter nos equipes', emoji: '📞' });
      actions.push({ id: 'en_savoir_plus', label: 'En savoir plus', emoji: 'ℹ️' });
      return actions;
    }

    if (lowerMessage.includes('cyber') || lowerMessage.includes('sécurité') || lowerMessage.includes('hack')) {
      actions.push({ id: 'contact_cyber', label: 'Contacter nos equipes', emoji: '📞' });
      actions.push({ id: 'evaluer_risques', label: 'Évaluer mes risques', emoji: '🔍' });
      return actions;
    }

    if (lowerMessage.includes('site') || lowerMessage.includes('web') || lowerMessage.includes('référencement')) {
      actions.push({ id: 'devis_site', label: 'Demander un devis', emoji: '📋' });
      actions.push({ id: 'voir_realisations', label: 'Voir nos réalisations', emoji: '👁️' });
      return actions;
    }

    if (lowerMessage.includes('contact') || lowerMessage.includes('parler') || lowerMessage.includes('rdv')) {
      actions.push({ id: 'formulaire_contact', label: 'Formulaire de contact', emoji: '📝' });
      actions.push({ id: 'appeler_won', label: 'Appeler directement', emoji: '📞' });
      return actions;
    }

    if (lowerMessage.includes('devis') || lowerMessage.includes('prix') || lowerMessage.includes('coût')) {
      actions.push({ id: 'demander_devis', label: 'Demander un devis', emoji: '📋' });
      actions.push({ id: 'planifier_rdv', label: 'Planifier un RDV', emoji: '📅' });
      return actions;
    }

    // Actions générales de progression
    actions.push({ id: 'comprendre_besoins', label: 'Analyser mes besoins', emoji: '🔍' });
    actions.push({ id: 'contact_general', label: 'Contacter WON', emoji: '📞' });

    return actions.slice(0, 2);
  }

  /**
   * Actions par défaut
   */
  private getDefaultActions(): QuickAction[] {
    return [
      { id: 'contact_expert', label: 'Parler à un expert', emoji: '👨‍💼' },
      { id: 'audit_gratuit', label: 'Audit gratuit', emoji: '📋' },
      { id: 'voir_services', label: 'Nos services', emoji: '🔍' }
    ];
  }

  /**
   * Réponse de fallback intelligente en cas d'erreur avec détection d'intention
   */
  private getFallbackResponse(userMessage: string): string {
    const lowerMessage = userMessage.toLowerCase();

    // Détection intention CONTACT - Réponse immédiate
    if (
      lowerMessage.includes('contacter won') ||
      lowerMessage.includes('contact won') ||
      lowerMessage.includes('joindre won') ||
      lowerMessage.includes('appeler won') ||
      (lowerMessage.includes('contact') && lowerMessage.includes('world')) ||
      lowerMessage === 'contact' ||
      lowerMessage === 'contacter' ||
      lowerMessage.includes('prendre contact') ||
      lowerMessage.includes('formulaire')
    ) {
      return `📞 **Contactez World Of Numeric :**

🌐 **Formulaire en ligne :** Utilisez le bouton "Ouvrir le formulaire" ci-dessous
📧 **Email :** contact@worldofnumeric.fr`;
    }
    // Analyse contextuelle pour réponse adaptée
    if (lowerMessage.includes('bâtiment') || lowerMessage.includes('devis') || lowerMessage.includes('chantier')) {
      return `Pour une entreprise du bâtiment comme la vôtre, la digitalisation est urgente ! 🏗️

**Priorités immédiates :**
• Site web professionnel pour la crédibilité
• Logiciel de devis pour gagner du temps
• Présence Google pour capter les prospects locaux

**ROI attendu :** 40h/mois économisées + 30% de prospects en plus

World Of Numeric accompagne 50+ entreprises du bâtiment. Contactez-nous pour un audit gratuit !`;
    }

    if (lowerMessage.includes('pme') && lowerMessage.includes('employés')) {
      const employees = userMessage.match(/(\d+)\s*employés?/i);
      const employeeCount = employees ? employees[1] : '30';

      return `Pour une PME de ${employeeCount} employés, la transformation digitale est stratégique ! 🚀

**Plan d'action recommandé :**
• Audit digital gratuit (diagnostic complet)
• Quick wins (site web + outils essentiels)
• Formation équipe (adoption des outils)

**Budget indicatif PME ${employeeCount} personnes :** 5-8k€ initial + 1-2k€/mois
**ROI typique :** Retour sur investissement en 6-12 mois

Commençons par un audit gratuit de votre situation !`;
    }

    // Réponse générale intelligente
    return `Excellente question sur la transformation digitale ! 💡

**Analyse de votre situation :**
Vous identifiez les bons enjeux. C'est le moment d'agir !

**Approche World Of Numeric :**
• Diagnostic gratuit de votre maturité digitale
• Roadmap personnalisée selon vos priorités
• Accompagnement étape par étape
• Formation équipe pour l'adoption

**Bénéfices clients similaires :**
• 40-60% de gain de temps sur les processus
• 25-40% d'augmentation de chiffre d'affaires

Un audit gratuit vous donnerait une vision claire. Intéressé ?`;
  }

  /**
   * Traite les actions rapides avec vraie redirection vers services
   */
  async handleQuickAction(actionId: string, actionLabel: string): Promise<ChatResponse> {
    // Actions avec redirection directe vers les vrais services
    const redirectActions: Record<string, ChatResponse> = {
      'contact_rgpd': {
        content: "Parfait ! Nos experts RGPD vous accompagnent pour votre mise en conformité.\n\n📧 **Contactez-nous directement :**\n• Email : rgpd@worldofnumeric.fr\n\n📝 **Ou remplissez notre formulaire :**\nCliquez sur 'Formulaire RGPD' ci-dessous.",
        quickActions: [
          { id: 'formulaire_rgpd', label: 'Formulaire RGPD', emoji: '📝' }
        ]
      },

      'contact_cyber': {
        content: "Nos experts cybersécurité analysent votre situation et vous proposent un plan de protection adapté.\n\n📧 **Contact direct :**\n• Email : cybersecurite@worldofnumeric.fr\n\n🔒 **Évaluation gratuite**",
        quickActions: [
          { id: 'formulaire_cyber', label: 'Demander évaluation', emoji: '🛡️' }
        ]
      },

      'devis_site': {
        content: "Nous créons votre site web sur-mesure ! Pour un devis précis, remplissez notre formulaire avec vos besoins.\n\n💻 **Nos créations :** www.worldofnumeric.fr/realisations\n📋 **Devis gratuit :** Formulaire ci-dessous",
        quickActions: [
          { id: 'formulaire_devis', label: 'Demander un devis', emoji: '📋' },
          { id: 'voir_portfolio', label: 'Voir nos sites', emoji: '👁️' }
        ]
      },

      'formulaire_contact': {
        content: "📝 **Formulaire de contact général :**\nwww.worldofnumeric.fr/contact\n\n✉️ **Email :** contact@worldofnumeric.fr",
        quickActions: [
          { id: 'ouvrir_formulaire', label: 'Ouvrir formulaire', emoji: '🌐' }
        ]
      },

      'appeler_won': {
        content: "📧 **World Of Numeric**\n**Email : contact@worldofnumeric.fr**\n\n💬 **Nos experts vous conseillent gratuitement !**",
        quickActions: [
          { id: 'rdv_rappel', label: 'Demander un rappel', emoji: '🔄' },
          { id: 'voir_horaires', label: 'Voir tous les horaires', emoji: '🕐' }
        ]
      },

      'comprendre_besoins': {
        content: "Pour vous conseiller au mieux, j'ai besoin d'en savoir plus sur votre entreprise :\n\n❓ **Quelques questions :**\n• Dans quel secteur travaillez-vous ?\n• Combien d'employés ?\n• Quel est votre principal défi numérique ?",
        quickActions: [
          { id: 'secteur_batiment', label: 'Secteur BTP', emoji: '🏗️' },
          { id: 'secteur_medical', label: 'Secteur médical', emoji: '🏥' },
          { id: 'secteur_commerce', label: 'Commerce/Retail', emoji: '🛒' },
          { id: 'autre_secteur', label: 'Autre secteur', emoji: '🏢' }
        ]
      }
    };

    // Si action prédéfinie, la retourner
    if (redirectActions[actionId]) {
      return redirectActions[actionId];
    }

    // Pour les actions du message d'accueil (RGPD, Cyber, etc.) - demander contexte
    const contextPrompts: Record<string, string> = {
      'rgpd': `L'utilisateur s'intéresse au RGPD mais je ne connais pas son contexte.
      Pose 1-2 questions pour comprendre : secteur d'activité, taille entreprise, si ils traitent des données sensibles.
      Adapte ensuite tes conseils RGPD selon sa situation. Sois bref et direct.`,

      'cybersec': `L'utilisateur s'inquiète de cybersécurité mais je ne connais pas son contexte.
      Demande : secteur, taille entreprise, s'ils ont déjà eu des incidents.
      Adapte ensuite tes conseils cyber selon sa situation.`,

      'facturation': `L'utilisateur s'intéresse à la facturation électronique mais je ne connais pas son contexte.
      Demande : taille entreprise (important pour les échéances légales), secteur, volume de factures.
      Adapte ensuite selon l'obligation qui s'applique à lui.`,

      'contact_general': `L'utilisateur veut contacter World Of Numeric.
      Donne les vrais contacts : téléphone, email, formulaire web.
      Propose aussi de comprendre son besoin pour le diriger vers le bon expert.`
    };

    const prompt = contextPrompts[actionId] ||
      `L'utilisateur a choisi "${actionLabel}". Ne suppose aucun contexte.
      Demande les informations nécessaires pour donner des conseils adaptés.`;

    return this.generateResponse(prompt);
  }

  /**
   * Réinitialise la conversation
   */
  resetConversation(): void {
    this.initializeConversation();
  }

  /**
   * Met à jour la configuration
   */
  updateConfig(newConfig: Partial<ChatbotConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Obtient l'historique de conversation (pour debug)
   */
  getConversationHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }
}

export default ChatbotAIService;
export type { ChatMessage, ChatResponse, QuickAction, ChatbotConfig };
